import React from 'react';
import { useRouter } from 'next/router';

import { ProductSearchCard, ProductSearchCardSkeleton } from 'components/product';
import {
  Button, Error, ImageMessage, Link, Popover, PopoverContent
} from 'components/ui';
import IconArrowNarrowRight from 'components/ui/icons/IconArrowNarrowRight';
import IconChevronDown from 'components/ui/icons/IconChevronDown';
import IconNotes from 'components/ui/icons/IconNotes';
import { IMAGES } from 'constants/images';
import { APP_ROUTES } from 'constants/routes';
import { checkIfMobile } from 'helpers/BrowserHelpers';
import { cx } from 'helpers/StylingHelpers';
import { useTranslation } from 'hooks/common/useTranslation';
import { useUsualProducts } from 'hooks/data/useUsualProducts';
import { useIsAuthenticated } from 'hooks/useIsAuthenticated';
import { useIsOpen } from 'hooks/useIsOpen';

import type { IUser } from 'types';

import styles from './UsualProductsPopoverDesktop.module.scss';

const MAX_PRODUCTS_COUNT = 6;

interface Props {
  className?: string,
  buttonClassName?: string,
  //
  user?: IUser,
  padding?: number
}

const UsualProductsPopoverDesktop = (props: Props) => {

  const {
    className,
    buttonClassName,
    //
    user,
    padding,
  } = props;

  // Hooks

  const router = useRouter();

  const { isOpen, toggle, close } = useIsOpen();

  // Effects

  const handleRouteChange = () => {
    close();
  };

  React.useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger

  const isMobile = checkIfMobile();

  const Trigger = (
    isMobile
      ? (
        <UsualProductsPopoverButton
          className={buttonClassName}
          label="NAVIGATION.MY_FRESHLIST"
          user={user}
          tag="button"
          onClick={toggle}
        />
      ) : (
        <Link href={APP_ROUTES.MY_FRESHLIST} prefetch={false} passHref>
          <UsualProductsPopoverButton
            className={buttonClassName}
            label="NAVIGATION.MY_FRESHLIST"
            user={user}
            tag="a"
          />
        </Link>
      )
  );

  // Unauthenticated

  if (!user) {
    return (
      <div className={className}>
        {Trigger}
      </div>
    );
  }

  // Render

  return (
    <Popover
      isOpen={isMobile ? isOpen : false}
      persist
      //
      className={cx(styles.popover, className)}
      contentClassName={cx(styles.popover)}
      //
      align="end"
      positions={['bottom']}
      padding={padding}
      //
      withArrow
      withHover={!isMobile}
      //
      content={(
        <UsualProductsPopoverContent user={user} />
      )}
      //
      onClickOutside={() => {
        if (isMobile) {
          close();
        }
      }}
    >
      {Trigger}
    </Popover>

  );
};

// Components

interface UsualProductsPopoverButtonProps {
  tag: 'a' | 'button',
  label: string,
  className?: string,
  user?: IUser,
  onClick?: () => void
}

const UsualProductsPopoverButton = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, UsualProductsPopoverButtonProps>((props, ref) => {
  const {
    className,
    tag,
    label,
    user,
    onClick,
  } = props;

  const { t } = useTranslation();

  return (
    <Button
      className={cx(
        styles.btn,
        className
      )}
      ref={ref}
      tag={tag}
      size="medium"
      startIcon={IconNotes}
      startIconProps={{ size: 22, strokeWidth: 1.5 }}
      endIcon={user ? IconChevronDown : undefined}
      endIconProps={user ? { size: 16, strokeWidth: 2 } : undefined}
      onClick={onClick}
    >
      {t(label)}
    </Button>
  );
});

const UsualProductsPopoverContent = (props: {user?: IUser}) => {
  const {
    user
  } = props;

  // Hooks

  const { t } = useTranslation();
  const { isAuthenticated } = useIsAuthenticated();

  const {
    isLoading, isError, isSuccess, data
  } = useUsualProducts({ page: 1, itemsPerPage: 11 }, {
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
  });

  // Props

  const {
    analyticsListId,
    analyticsListName,
    items = []
  } = data || {};

  const products = React.useMemo(() => {
    if (!items) return [];
    return items.slice(0, MAX_PRODUCTS_COUNT + 1);
  }, [items]);

  // Render

  return (
    <PopoverContent
      className={styles.popoverContent}
      actionsClassName={styles.popoverContentActions}
      title="NAVIGATION.USUAL_PRODUCTS"
      actions={(
        <>
          <Link href={APP_ROUTES.MY_FRESHLIST} prefetch={false} passHref>
            <Button
              tag="a"
              variant="empty"
              endIcon={IconArrowNarrowRight}
              className={styles.popoverActionButton}
              size="large"
            >
              {t('MY_FRESHLIST.VIEW_MORE')}
            </Button>
          </Link>
        </>
      )}
    >

      {/* Loading */}
      {
        isLoading && Array.from(Array(MAX_PRODUCTS_COUNT)).map((_, index) => (
          <ProductSearchCardSkeleton key={index} />
        ))
      }

      {/* Error */}
      {
        isError && (
          <Error small />
        )
      }

      {/* Success */}
      {
        isSuccess && (
          !products?.length
            ? (
              <ImageMessage
                className={styles.emptyState}
                image={IMAGES.MY_FRESHLIST.EMPTY}
                imageProps={{
                  width: 538,
                  height: 313,
                  alt: 'No Products'
                }}
                title={t('MY_FRESHLIST.EMPTY_TITLE')}
                subtitle={t('MY_FRESHLIST.EMPTY_SUBTITLE')}
                titleClassName={styles.emptyStateTitle}
                subtitleClassName={styles.emptyStateSubtitle}
              />
            ) : (
              !!products?.length && (
                <div className={styles.products}>
                  {
                    products.map((product, index) => {
                      return (
                        <ProductSearchCard
                          key={product?.variantCode || index}
                          product={product}
                          listProps={{
                            index,
                            analyticsListId,
                            analyticsListName
                          }}
                          bypassQuantityVariants
                          shouldShowGeniusPrice={user?.shouldShowGeniusPrice}
                        />
                      );
                    })
                  }
                </div>
              )
            )
        )
      }

    </PopoverContent>
  );
};

const ChevronIcon = <IconChevronDown size={16} strokeWidth={2} />;

// Export

export default React.memo(UsualProductsPopoverDesktop);
